/* global google */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import Image from 'next/image';
import { Map, useMap, useMapsLibrary } from '@vis.gl/react-google-maps';

import SvgSearch from '@/icons/Search';
import { CloseSquare } from '@/components/SvgComponents';

import SearchList from '../SearchList/SearchList';

import styles from './GoogleMap.module.scss';

interface IProps {
  lat?: number;
  lng?: number;
  zoom?: number;
  center?: { lat: number; lng: number };
  customMarker: string;
  address: {
    address_coordinates: string;
    address: string;
  };
  setAddress: React.Dispatch<
    React.SetStateAction<{
      address_coordinates: string;
      address: string;
    }>
  >;
  setMapCenter: React.Dispatch<
    React.SetStateAction<{ lat: number; lng: number }>
  >;
}

const GoogleMap = ({
  customMarker,
  setMapCenter,
  address,
  setAddress,
}: IProps) => {
  const geocodingApiLoaded = useMapsLibrary('geocoding');
  const placesApiLoaded = useMapsLibrary('places');
  const map = useMap('delivery');
  const [addressValue, setAddressValue] = useState<string>('');
  const [geocoding, setGeocoding] = useState<google.maps.Geocoder>();
  const [isCenterChanging, setIsCenterChanging] = useState(false);
  const [searchResults, setSearchResults] = useState<
    google.maps.places.AutocompletePrediction[]
  >([]);

  const autocompleteRef = useRef<HTMLInputElement>(null);
  const selectedPlaceIdRef = useRef<string>('');

  const handleInputChange = (value: string) => {
    if (typeof window === 'undefined' || !google?.maps?.places) {
      setSearchResults([]);
      return;
    }
    const autocompleteService = new google.maps.places.AutocompleteService();

    const options = {
      componentRestrictions: { country: 'az' },
      fields: ['address_components', 'geometry', 'icon', 'name'],
      strictBounds: false,
    };

    if (value) {
      autocompleteService.getPlacePredictions(
        { input: value, ...options },
        (predictions, status) => {
          if (
            status === google.maps.places.PlacesServiceStatus.OK &&
            predictions
          ) {
            setSearchResults(predictions);
          } else {
            setSearchResults([]);
          }
        },
      );
    } else {
      setSearchResults([]);
    }
  };

  const debounce = (func: (...args: any[]) => void, delay: number) => {
    let timeoutId: ReturnType<typeof setTimeout>;

    const debouncedFunction = (...args: any[]) => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      timeoutId = setTimeout(() => {
        func(...args);
      }, delay);
    };

    // Add cancel method to the debounced function
    debouncedFunction.cancel = () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };

    return debouncedFunction;
  };

  const debouncedHandleInputChange = useRef(
    debounce((value: string) => {
      handleInputChange(value);
    }, 500),
  ).current;

  const handlePlaceSelection = (placeId: string, description: string) => {
    debouncedHandleInputChange.cancel();
    if (map) {
      const placesService = new placesApiLoaded.PlacesService(map);

      placesService.getDetails({ placeId }, (place) => {
        if (place && place.geometry) {
          selectedPlaceIdRef.current = placeId;
          map.setCenter(place.geometry.location);
          map.setZoom(15);
          setAddressValue(description || '');
          setAddress({
            address_coordinates: `https://maps.google.com/?q=${place.geometry.location.lat()},${place.geometry.location.lng()}`,
            address: description,
          });
          setSearchResults([]);
        }
      });
    }
  };

  const handleResultClick = (placeId: string, description: string) => {
    handlePlaceSelection(placeId, description);
  };

  const handleClearInput = useCallback(() => {
    setAddress(null);
    setAddressValue('');
    selectedPlaceIdRef.current = '';
    setSearchResults([]);
  }, []);

  useEffect(() => {
    if (!geocodingApiLoaded) return;
    setGeocoding(new google.maps.Geocoder());
  }, [geocodingApiLoaded]);

  useEffect(() => {
    return () => {
      debouncedHandleInputChange.cancel();
    };
  }, [debouncedHandleInputChange]);

  const onMapDrag = (event) => {
    const center = event.map.getCenter().toJSON();

    setMapCenter(center);
    setIsCenterChanging(false);
    setSearchResults([]);
    geocoding
      .geocode({
        location: {
          lng: center.lng,
          lat: center.lat,
        },
      })
      .then((data) => {
        const formattedAddress = data?.results[0]?.formatted_address || '';
        setAddressValue(formattedAddress);
        setAddress({
          address_coordinates: `https://maps.google.com/?q=${center.lat},${center.lng}`,
          address: formattedAddress,
        });
      });
  };

  return (
    <>
      <div
        className={classNames([
          styles.inputWrapper,
          {
            [styles.wrapperFullHeight]: searchResults.length > 0,
          },
        ])}
        itemScope
        itemType='https://schema.org/GeoCoordinates'
      >
        <input
          ref={autocompleteRef}
          className={styles.searchInput}
          placeholder='Bakı şəh., Təbriz küç. 108'
          value={addressValue}
          onChange={(e) => {
            setAddressValue(e.target.value);
            debouncedHandleInputChange(e.target.value);
          }}
          aria-label={addressValue}
        />
        {address ? (
          <CloseSquare
            className={styles.inputIcon}
            onClick={handleClearInput}
          />
        ) : (
          <SvgSearch className={styles.inputIcon} />
        )}
        <SearchList
          places={searchResults}
          onSelect={handleResultClick}
          selectedPlaceId={selectedPlaceIdRef.current}
        />
      </div>
      <div className={styles.wrap}>
        <Map
          id='delivery'
          defaultZoom={12}
          defaultCenter={{
            lat: 40.409264,
            lng: 49.867092,
          }}
          onDragend={onMapDrag}
          disableDefaultUI
        />
        <div
          className={classNames([
            styles.marker,
            { [styles.isScaled]: isCenterChanging },
          ])}
        >
          <Image src={customMarker} alt='marker' width={45} height={45} />
        </div>
      </div>
    </>
  );
};

export default GoogleMap;
